import { Injectable, Injector } from '@angular/core';
import { ChatTabsUsersService } from '@components/chat/chat-tabs-users/chat-tabs-users.service';
import { AppEventsE } from '@enums/events/app-events.enum';
import { ChatEventsNameE } from '@enums/events/chat-events-name';
import { LoginEventsNameE } from '@enums/events/login-event-names';
import { environment } from '@environments/environment';
import { AppUpdateHelperService } from '@helpers/app-update-helper.service';
import { UserHelperService } from '@helpers/user-helper.service';
import { CustomerAgentsI, UserAttributesI, UserLoginI, UserSocketLoginI } from '@interfaces/user.interface';
import { AgentBlockInfoI } from '@interfaces/agent-block.interface';
import { UserStoreService } from '@modules/chat/store/user-store.service';
import { MaintenanceNotificationI } from '@modules/maintenance-notification/interfaces/maintenance-notification.interface';
import { TranslateService } from '@ngx-translate/core';
import { SessionStorageService } from '@services/common/session-storage.service';
import { SipConnectionManagerService } from '@services/common/sip-connection-manager.service';
import { UsersHttpService } from '@services/users/users-http.service';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { BehaviorSubject, debounceTime, firstValueFrom, Subject } from 'rxjs';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';

@Injectable({
  providedIn: 'root',
})
export class LoginSocketService {
  private static readonly HEADER_EXTERNAL_LOGIN_ID = 'x-external-login';
  private static readonly HEADER_USER_ID = 'x-user-id';
  private static readonly HEADER_SESSION_ID = 'x-session-id';
  private static readonly HEADER_CUSTOMER_ID = 'x-customer-id';
  private static readonly HEADER_ACCESS_LEVEL = 'x-access-level';

  private socketUrl = environment.OMNISMART_SOCKET_URL;
  private namespace = 'login';
  public socket!: Socket;
  private socketOptions: Partial<ManagerOptions & SocketOptions> = {
    reconnection: true,
    timeout: 5000,
  };
  private version = new BehaviorSubject<string>('');
  public version$ = this.version.asObservable();

  private newVersion = new BehaviorSubject<boolean>(false);
  public newVersion$ = this.newVersion.asObservable();
  private allUsersSubject = new BehaviorSubject<CustomerAgentsI | null>(null);
  public allUsers$ = this.allUsersSubject.asObservable();

  private logoffReminderSubject = new Subject<{ logoffReminder: number } | null>();
  public newLogoffReminder$ = this.logoffReminderSubject.asObservable();

  private maintenanceNotifications = new BehaviorSubject<MaintenanceNotificationI[]>([]);
  public maintenanceNotifications$ = this.maintenanceNotifications.asObservable();

  private newMaintenanceNotification = new Subject<MaintenanceNotificationI>();
  public newMaintenanceNotification$ = this.newMaintenanceNotification.asObservable();

  private currentBlock = new BehaviorSubject<AgentBlockInfoI | null>(null);
  public currentBlock$ = this.currentBlock.asObservable();

  private user!: UserSocketLoginI | void;
  private userData = this.userHelperService.getSocketLoginData();
  private initialized = false;

  constructor(
    private appUpdateHelperService: AppUpdateHelperService,
    private userStoreService: UserStoreService,
    private sessionStorageService: SessionStorageService,
    private userHelperService: UserHelperService,
    private sipConnectionManager: SipConnectionManagerService,
    private injector: Injector,
    private translateService: TranslateService,
    private userService: UsersHttpService,
    private readonly chatTabUser: ChatTabsUsersService
  ) {}

  public init() {
    const user = this.userHelperService.getUserAttributes() as UserAttributesI;
    if (!this.socket && user) {
      const namespacePrefix = this.socketUrl.endsWith('/') ? '' : '/';
      this.socket = io(this.socketUrl + namespacePrefix + this.namespace, {
        ...this.socketOptions,
        extraHeaders: {
          [LoginSocketService.HEADER_EXTERNAL_LOGIN_ID]: user.admin_user ? 'true' : 'false',
          [LoginSocketService.HEADER_USER_ID]: user['custom:userId'],
          [LoginSocketService.HEADER_CUSTOMER_ID]: user['custom:customer'],
          [LoginSocketService.HEADER_ACCESS_LEVEL]: user.level?.toString() || '0',
          [LoginSocketService.HEADER_SESSION_ID]: this.sessionStorageService.getSessionId()!,
        },
      });
      this.socket.connect();
    }

    this.initEvents();
    return this.socket;
  }

  private initEvents() {
    if (!this.initialized) {
      this.userHelperService.subscribeUserChange((newUser: UserAttributesI) => {
        if (newUser) {
          this.loadUserData(newUser);
        }
      });
      const userAttributes = this.userHelperService.getUserAttributes();
      this.user = this.getUserData(userAttributes);
      this.userData = this.userHelperService.attributesAsUserSocketLoginI(userAttributes!);

      this.socket.emit(ChatEventsNameE.LOGIN_USER, this.userData);

      this.socket.io.on('reconnect', () => {
        this.socket.emit(ChatEventsNameE.LOGIN_USER, this.userData);
      });

      this.loadUserList().then(() => {
        this.askSocketForAllAgentsStatus();
      });

      this.listenAllUsers();
      this.listenLoginUser();
      this.listenNewVersion();
      this.listenMaintenanceNotification();
      this.listenPabxEnabledEvent();
      this.listenOnlineUser();
      this.listenOfflineUser();
      this.listenAwayUser();
      this.listenBackUser();
      this.listenAgentBlocked();
      this.listenAgentUnblocked();
      this.listenLogoffReminders();
      this.initialized = true;
    }
  }

  public emitUserActivity(): void {
    if (this.socket?.connected) {
      this.socket.emit(LoginEventsNameE.USER_ACTIVITY);
    }
  }

  private listenAwayUser(): void {
    this.listenSocketEvent(LoginEventsNameE.USER_AWAY, (userId: string) => {
      if (userId) {
        this.userStoreService.setUserAway(userId);
      }
    });
  }

  private listenBackUser(): void {
    this.listenSocketEvent(LoginEventsNameE.USER_BACK, (userId: string) => {
      if (userId) {
        this.userStoreService.setUserBack(userId);
      }
    });
  }

  private listenAgentBlocked(): void {
    this.listenSocketEvent(LoginEventsNameE.AGENT_BLOCKED, (data: AgentBlockInfoI) => {
      this.currentBlock.next(data ?? {});
    });
  }

  private listenAgentUnblocked(): void {
    this.listenSocketEvent(LoginEventsNameE.AGENT_UNBLOCKED, () => {
      this.currentBlock.next(null);
    });
  }

  private listenOnlineUser(): void {
    this.listenSocketEvent(LoginEventsNameE.ONLINE_USER, (userId: string) => {
      if (userId) {
        this.userStoreService.setUserOnline(userId);
      }
    });
  }

  private listenOfflineUser(): void {
    this.listenSocketEvent(LoginEventsNameE.OFFLINE_USER, (userId: string) => {
      if (userId) {
        this.userStoreService.setUserOffline(userId);
      }
    });
  }

  private askSocketForAllAgentsStatus(): void {
    try {
      if (this.user) {
        this.allUsers$.subscribe((res) => {
          if (res) {
            const { currentOnlineUsers } = res;
            const onlineUsers = res.users ?? [];
            this.userStoreService.setUsersOnline({ onlineUsers, currentOnlineUsers });
          }
        });
        this.socket.emit(LoginEventsNameE.ALL_USERS, this.user);
      }
    } catch (error) {
      throw new Error("Can't emit all agents");
    }
  }

  private listenLogoffReminders() {
    try {
      this.socket.on('logoff-reminder', (data) => {
        this.logoffReminderSubject.next(data);
      });
    } catch (error) {}
  }

  private async loadUserList(): Promise<void> {
    const users = (await firstValueFrom(this.userService.getUsers())).data;
    const onlineUsers = await firstValueFrom(this.userStoreService.listOnlineUsersId$);
    const usersToAdd = users
      .filter((user) => user._id && user.status !== 'inactive')
      .map((user) => {
        const isUserAlreadyOnline = onlineUsers.includes(user?._id);
        user = this.chatTabUser.setActivityStatus(user, isUserAlreadyOnline);
        return user;
      });
    if (usersToAdd?.length) {
      this.userStoreService.addNewUsers(usersToAdd);
    }
  }

  private listenAllUsers() {
    try {
      this.socket.on(LoginEventsNameE.ALL_USERS, (data) => {
        this.allUsersSubject.next(data);
      });
    } catch (error) {}
  }

  private loadUserData(newAttributes: UserAttributesI | void) {
    let attributes = newAttributes;
    if (!attributes) {
      attributes = this.userHelperService.getUserAttributes();
    }
    if (attributes) {
      this.user = this.getUserData(attributes);
      this.userData = this.userHelperService.attributesAsUserSocketLoginI(attributes);
    }
  }
  public getUserData(user: UserAttributesI | void): UserSocketLoginI | void {
    if (!user) return;
    return {
      _id: user['custom:userId'],
      customerId: user['custom:customer'],
      teams: user['custom:team'],
      team: user['custom:team'],
    } as UserSocketLoginI;
  }

  private listenPabxEnabledEvent() {
    this.socket.on('pabx-enabled', (data) => this.sipConnectionManager.onSipEnabledEvent(data));
    const toastrService = this.injector.get(ToastrService);
    this.sipConnectionManager.alert$.pipe(debounceTime(1000)).subscribe((data) => {
      if (data) {
        const message = this.translateService.instant(data.message);
        const title = this.translateService.instant(data.title);
        const toastrOptions: Partial<IndividualConfig> = {
          positionClass: 'toast-bottom-right',
          progressBar: true,
          closeButton: true,
          timeOut: 6000,
        };

        if (data.warning) {
          toastrService.warning(message, title, toastrOptions);
        } else {
          toastrService.success(message, title, toastrOptions);
        }
      }
    });
  }

  public restart() {
    if (this.socket) {
      this.socket.close();
      this.socket.connect();
    }

    return this.socket;
  }

  private listenLoginUser(): void {
    try {
      this.listenSocketEvent(LoginEventsNameE.LOGIN_USER, (user: UserLoginI) => {
        this.version.next(user.applications![0].version);

        if (user.notifications && user.notifications.length) {
          user.notifications.forEach((notification) => {
            this.appUpdateHelperService.executeMaintenanceNotification(notification);
          });

          this.maintenanceNotifications.next(user.notifications);
        }
      });
    } catch (error) {
      throw new Error("Can't listen login-user event");
    }
  }

  private listenNewVersion(): void {
    try {
      this.listenSocketEvent(AppEventsE.NEW_VERSION, () => {
        this.appUpdateHelperService.executeUpdate();
        this.newVersion.next(true);
      });
    } catch (error) {
      throw new Error("Can't listen new-version event");
    }
  }

  private listenMaintenanceNotification(): void {
    try {
      this.listenSocketEvent(AppEventsE.MAINTENANCE_NOTIFICATION, (notification: MaintenanceNotificationI) => {
        this.appUpdateHelperService.executeMaintenanceNotification(notification);
        this.newMaintenanceNotification.next(notification);
      });
    } catch (error) {
      throw new Error("Can't listen maintenance-notification event");
    }
  }

  private async listenSocketEvent(event: string, handler: (...args) => void) {
    try {
      this.socket.off(event);
      this.socket.on(event, async (...args) => {
        handler(...args);
      });
    } catch (error) {
      throw new Error(`Can't listen ${event} event`);
    }
  }
}
